/**
 * Marks a configuration row (an Additional Field, an incident type, a category, an option) as
 * "added in this editing session but not yet saved".
 *
 * Why a Symbol, and why the marker lives inside Formik `values`: these lists decide whether a row's
 * Key may still be edited, and neither of the obvious discriminators is a stable identity. The array
 * position is not - rows are keyed by position, so a reorder shuffles data between positions. The
 * key string is not either - it is re-derived from the Display Name on every keystroke while a row
 * is new, so a new row whose key happens to spell a saved row's key reads as saved and locks itself
 * out permanently. A marker stamped on the row object at creation is stable under both, because it
 * travels with the object through every move, sort and splice.
 *
 * The key is a Symbol precisely so that `JSON.stringify` drops it: every save path here serializes
 * `values` through axios, so the marker physically cannot reach an organization's stored
 * configuration and no payload builder needs a strip step. It survives the operations Formik
 * performs because `setIn` shallow-clones along the path with lodash `clone` and `arrayHelpers.push`
 * uses lodash `cloneDeep`, both of which copy own enumerable symbols. `newRowIdentity.test.ts` pins
 * all of that, so a dependency upgrade that changes it fails loudly rather than silently locking
 * rows again.
 */
const NEW_ROW: unique symbol = Symbol('newConfigurationRow');

type NewRow = { [NEW_ROW]: true };
type MaybeNewRow = Partial<NewRow>;

export const markNewRow = <T extends object>(row: T): T & NewRow => ({ ...row, [NEW_ROW]: true });

export const isNewRow = (row: unknown): boolean =>
  typeof row === 'object' && row !== null && (row as MaybeNewRow)[NEW_ROW] === true;
